import { RefObject, useEffect, useRef, useState } from 'react';
import type { ImageTargetData } from '../types/xr8';
import { configureXR8ImageTargets, loadImageTarget } from '../utils/imageTarget';
import { ensureXrController, registerXrExtras } from '../utils/xr8Ready';

export const useXR8Scene = (
    targetJsonUrl: string,
    sceneRef: RefObject<HTMLElement>,
    enabled = true,
) => {
    const [ready, setReady] = useState(false);
    const [targetName, setTargetName] = useState<string | null>(null);
    const targetRef = useRef<ImageTargetData | null>(null);

    useEffect(() => {
        if (!enabled) {
            setReady(false);
            setTargetName(null);
            targetRef.current = null;
            return;
        }

        let cancelled = false;
        setReady(false);
        setTargetName(null);
        targetRef.current = null;

        const setup = async () => {
            await ensureXrController();
            if (cancelled) return;

            registerXrExtras();
            const target = await loadImageTarget(targetJsonUrl);
            if (cancelled) return;

            targetRef.current = target;
            configureXR8ImageTargets([target]);
            setTargetName(target.name);
            setReady(true);
        };

        setup().catch((error) => {
            if (cancelled) return;
            console.error('[XR8] Setup failed:', error);
        });

        return () => {
            cancelled = true;
        };
    }, [targetJsonUrl, enabled]);

    useEffect(() => {
        if (!enabled || !ready || !targetRef.current || !sceneRef.current) return;

        const scene = sceneRef.current as HTMLElement & { hasLoaded?: boolean };
        const applyTargets = () => {
            if (!targetRef.current) return;
            configureXR8ImageTargets([targetRef.current]);
        };

        if (scene.hasLoaded) {
            applyTargets();
        } else {
            scene.addEventListener('loaded', applyTargets, { once: true });
        }
    }, [enabled, ready, sceneRef, targetJsonUrl]);

    return { ready, targetName };
};
